import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface UseRecordingUploadReturn {
  uploadRecording: (blob: Blob, contentId: string) => Promise<boolean>;
  isUploading: boolean;
  uploadProgress: number;
}

export function useRecordingUpload(): UseRecordingUploadReturn {
  const { user } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadRecording = async (blob: Blob, contentId: string): Promise<boolean> => {
    if (!user) {
      toast.error("Please sign in to save recording");
      return false;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Generate storage path
      const fileExtension = blob.type.includes("webm") ? "webm" : "ogg";
      const storagePath = `${user.id}/${contentId}.${fileExtension}`;

      // Upload to Supabase Storage
      setUploadProgress(20);
      const { error: uploadError } = await supabase.storage
        .from("content-uploads")
        .upload(storagePath, blob, {
          contentType: blob.type,
          upsert: true,
        });

      if (uploadError) throw uploadError;

      setUploadProgress(60);

      // Update content record with storage path
      const { error: updateError } = await supabase
        .from("content")
        .update({
          storage_path: storagePath,
          transcript_status: "pending",
        })
        .eq("id", contentId);

      if (updateError) throw updateError;

      setUploadProgress(80);

      // Trigger transcript extraction
      const { error: transcriptError } = await supabase.functions.invoke(
        "extract-media-transcript",
        {
          body: { contentId },
        }
      );

      if (transcriptError) {
        console.error("Transcript generation failed:", transcriptError);
        // Don't fail the upload, just log the error
      }

      setUploadProgress(100);
      toast.success("Recording saved successfully");
      return true;
    } catch (error) {
      console.error("Error uploading recording:", error);
      toast.error("Failed to save recording");
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadRecording,
    isUploading,
    uploadProgress,
  };
}
